import { type FC } from "react";
import { Toolbar, Checkbox, Button, Typography, Divider } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { MutationErrorLike } from "@/partner/shared/lib";
import { ToolbarProgress, useConfirmAction } from "@/shared/components";
import {
  marketingErrorKey,
  useArchiveAutomationsMutation,
  useDeleteAutomationsMutation,
} from "@/partner/entities/marketing";

interface BroadcastsListToolbarProps {
  indeterminate: boolean;
  allSelected: boolean;
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedIds: string[];
  /** Подмножество selectedIds, которое реально уйдёт в архив: без
   * запланированных и отправляющихся рассылок (см. BroadcastsList.tsx). */
  archivableIds: string[];
  onCloseSelected: () => void;
}

/**
 * Тулбар выбранных рассылок: в архив и удаление, как
 * EmailTemplatesListToolbar. Удалить можно только черновики, отказ
 * бэкенда (AUTOMATION_NOT_DELETABLE) приходит снекбаром. В архив уходят
 * только archivableIds: запланированные и отправляющиеся среди выбранных
 * пропускаются, чтобы массовая уборка списка случайно не остановила
 * рассылку в процессе (BroadcastService.archiveAutomations переводит её
 * активные enrollment-ы в EXITED).
 */
export const BroadcastsListToolbar: FC<BroadcastsListToolbarProps> = ({
  indeterminate,
  allSelected,
  numSelected,
  onSelectAllClick,
  selectedIds,
  archivableIds,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const onError = (error: MutationErrorLike) =>
    enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" });
  const [archiveBroadcasts, { loading: archiving }] =
    useArchiveAutomationsMutation();
  const [deleteBroadcasts, { loading: deleting }] =
    useDeleteAutomationsMutation();
  const loading = archiving || deleting;

  const onArchiveSelected = () => {
    confirm({
      title: t("broadcasts.bulk.archive"),
      text: (
        <>
          {t("broadcasts.bulk.confirmArchive")}
          <br />
          {t("broadcasts.bulk.archiveSkipsActive")}
        </>
      ),
      onConfirm: () =>
        archiveBroadcasts({
          variables: { ids: archivableIds },
          onCompleted: () => {
            onCloseSelected();
            enqueueSnackbar(t("broadcasts.send.archived"), {
              variant: "success",
            });
          },
          onError,
        }),
    });
  };

  const onDeleteSelected = () => {
    confirm({
      title: t("broadcasts.bulk.delete"),
      text: t("broadcasts.bulk.confirmDelete"),
      onConfirm: () =>
        deleteBroadcasts({
          variables: { ids: selectedIds },
          onCompleted: () => {
            onCloseSelected();
            enqueueSnackbar(t("broadcasts.bulk.deleted"), {
              variant: "success",
            });
          },
          onError,
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={allSelected}
        onChange={onSelectAllClick}
        sx={{ marginLeft: "-16px", marginRight: { xs: 0, sm: "16px" } }}
      />
      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>
      <Divider
        orientation="vertical"
        variant="middle"
        sx={{ mx: 1.25, height: "2em" }}
      />
      <Button
        color="inherit"
        onClick={onArchiveSelected}
        disabled={loading || archivableIds.length === 0}
      >
        {t("broadcasts.bulk.archive")}
      </Button>
      <Button
        color="inherit"
        onClick={onDeleteSelected}
        disabled={loading || numSelected === 0}
      >
        {t("broadcasts.bulk.delete")}
      </Button>
      <ToolbarProgress loading={loading} />
      {confirmDialog}
    </Toolbar>
  );
};
